/**
 * AppShell — authenticated app chrome.
 *
 * Topbar  : logo + Cmd-K search stub + language picker + user menu
 * Sidebar : sectioned nav, admin section gated by role
 * Footer  : status pulse only (no internal build metadata exposed to users)
 *
 * Page content rendered by routes via <Outlet />.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, Activity, Radio, Coins, History, CalendarClock,
  Grid3X3, Telescope, FlaskConical, CandlestickChart,
  Users, Settings, UserCog, LogOut, Send,
  BarChart3, Link2, Store, Menu, X, Globe2,
} from 'lucide-react'
import { Logo } from '@/components/ui/Logo'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { AccountSwitcher } from '@/components/layout/AccountSwitcher'
import { BybitKeyBanner } from '@/components/layout/BybitKeyBanner'
import { AdminBybitKeysBanner } from '@/components/layout/AdminBybitKeysBanner'
import { useAuthStore } from '@/stores/auth'
import { useLogout } from '@/hooks/useAuthApi'
import { cn } from '@/lib/cn'

export function AppShell() {
  const user = useAuthStore((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const logout = useLogout()
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()

  // Route change (nav link tapped) closes the mobile drawer.
  useEffect(() => { setNavOpen(false) }, [location.pathname])

  // Lock body scroll while the mobile nav drawer is open.
  useEffect(() => {
    if (!navOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [navOpen])

  return (
    <div className="min-h-screen flex flex-col bg-ink">
      <Topbar
        onLogout={logout}
        userEmail={user?.email ?? ''}
        navOpen={navOpen}
        onToggleNav={() => setNavOpen((v) => !v)}
      />

      {/* PULSE-28: global Bybit key health banner, only renders when
          there's an actionable issue (red/yellow alerts). Sits between
          chrome and content so it can't be missed. */}
      <BybitKeyBanner />
      {/* PULSE-417: admin sees dead/expiring keys of ALL accounts. */}
      {isAdmin && <AdminBybitKeysBanner />}

      <div className="flex-1 flex">
        {/* Static sidebar ≥ lg */}
        <div className="hidden lg:block shrink-0">
          <Sidebar isAdmin={isAdmin} />
        </div>

        {/* Mobile off-canvas nav < lg */}
        {navOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
            <div
              className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
              onClick={() => setNavOpen(false)}
            />
            <div className="absolute top-0 left-0 h-full w-64 max-w-[85vw] bg-surface border-r border-line overflow-y-auto">
              <div className="flex items-center justify-between px-4 h-12 border-b border-line">
                <Logo />
                <button
                  onClick={() => setNavOpen(false)}
                  className="text-textDim hover:text-text p-1 -mr-1"
                  aria-label="Close menu"
                >
                  <X size={18} />
                </button>
              </div>
              <Sidebar isAdmin={isAdmin} />
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>

      <FooterStatus />
    </div>
  )
}

// ─── Topbar ──────────────────────────────────────────────────────────

function Topbar({
  onLogout, userEmail, navOpen, onToggleNav,
}: {
  onLogout: () => void
  userEmail: string
  navOpen: boolean
  onToggleNav: () => void
}) {
  const { t } = useTranslation('common')
  const initial = (userEmail[0] ?? 'U').toUpperCase()
  return (
    <header className="border-b border-line flex items-center px-3 sm:px-5 h-12 shrink-0 gap-2">
      <button
        onClick={onToggleNav}
        className="lg:hidden text-textDim hover:text-text p-1.5 -ml-1.5"
        aria-label="Menu"
        aria-expanded={navOpen}
      >
        {navOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      <Link to="/app" className="sm:pr-4 sm:border-r border-line sm:mr-4 shrink-0">
        <Logo />
      </Link>

      <div className="ml-1 flex-1 max-w-md hidden md:block">
        <div className="flex items-center gap-2 px-3 py-1 bg-elevated border border-line rounded-md text-textFaint">
          <span>⌕</span>
          <span className="text-xs">{t('actions.search')}</span>
          <span className="ml-auto kbd">⌘ K</span>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3 text-xs text-textDim min-w-0">
        <AccountSwitcher />
        <div className="hidden sm:block"><LanguageSwitcher /></div>
        <div className="w-px h-5 bg-line mx-1 hidden sm:block" />
        <span className="hidden xl:inline">{userEmail}</span>
        <div
          className="w-7 h-7 bg-elevated grid place-items-center rounded-md text-xs font-medium shrink-0 hidden sm:grid"
          title={userEmail}
        >
          {initial}
        </div>
        <button
          onClick={onLogout}
          className="text-textFaint hover:text-text p-1 shrink-0"
          aria-label={t('actions.log_out') as string}
          title={t('actions.log_out') as string}
        >
          <LogOut size={16} />
        </button>
      </div>
    </header>
  )
}

// ─── Sidebar ─────────────────────────────────────────────────────────

interface NavItem {
  to: string
  labelKey: string
  icon: ReactNode
}

function Sidebar({ isAdmin }: { isAdmin: boolean }) {
  const { t } = useTranslation('common')
  const live: NavItem[] = [
    { to: '/app',           labelKey: 'nav.dashboard', icon: <LayoutDashboard size={14} /> },
    { to: '/app/positions', labelKey: 'nav.positions', icon: <Activity size={14} /> },
    { to: '/app/signals',   labelKey: 'nav.signals',   icon: <Radio size={14} /> },
    { to: '/app/discovery', labelKey: 'nav.discovery', icon: <Telescope size={14} /> },
    { to: '/app/analytics', labelKey: 'nav.analytics', icon: <BarChart3 size={14} /> },
    { to: '/app/lab',       labelKey: 'nav.lab',       icon: <FlaskConical size={14} /> },
    { to: '/app/catalog',   labelKey: 'nav.catalog',   icon: <Store size={14} /> },
    { to: '/app/screeners', labelKey: 'nav.screeners', icon: <Send size={14} /> },
  ]
  const data: NavItem[] = [
    { to: '/app/symbols', labelKey: 'nav.symbols', icon: <Coins size={14} /> },
    { to: '/app/history', labelKey: 'nav.history', icon: <History size={14} /> },
    { to: '/app/events',  labelKey: 'nav.events',  icon: <CalendarClock size={14} /> },
  ]
  const account: NavItem[] = [
    { to: '/app/settings', labelKey: 'nav.my_settings', icon: <UserCog size={14} /> },
  ]
  const adminItems: NavItem[] = [
    { to: '/app/slots',          labelKey: 'nav.slots',    icon: <Grid3X3 size={14} /> },
    { to: '/app/admin/users',    labelKey: 'nav.users',    icon: <Users size={14} /> },
    { to: '/app/admin/assignments', labelKey: 'nav.assignments', icon: <Link2 size={14} /> },
    { to: '/app/admin/settings', labelKey: 'nav.settings', icon: <Settings size={14} /> },
    { to: '/app/market',         labelKey: 'nav.market',    icon: <CandlestickChart size={14} /> },
    { to: '/app/friend-trades',  labelKey: 'nav.friend_trades', icon: <CandlestickChart size={14} /> },
    { to: '/app/atlas',          labelKey: 'nav.atlas',     icon: <Globe2 size={14} /> },
  ]

  return (
    <nav className="w-full lg:w-52 lg:border-r border-line py-3 px-2 text-sm shrink-0">
      <Section title={t('nav.live')}>
        {live.map((i) => <NavRow key={i.to} item={i} end={i.to === '/app'} />)}
      </Section>

      <Section title={t('nav.data')}>
        {data.map((i) => <NavRow key={i.to} item={i} />)}
      </Section>

      <Section title={t('nav.account')}>
        {account.map((i) => <NavRow key={i.to} item={i} />)}
      </Section>

      {isAdmin && (
        <Section title={t('nav.admin')}>
          {adminItems.map((i) => <NavRow key={i.to} item={i} />)}
        </Section>
      )}
    </nav>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-6">
      <div className="text-textFaint text-2xs font-medium uppercase tracking-wider px-2.5 mb-1.5">
        {title}
      </div>
      {children}
    </div>
  )
}

function NavRow({ item, end }: { item: NavItem; end?: boolean }) {
  const { t } = useTranslation('common')
  return (
    <NavLink
      to={item.to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 px-2.5 py-2 rounded-md font-medium transition-colors',
          isActive
            ? 'bg-fire-faint text-fire'
            : 'text-textDim hover:text-text hover:bg-elevated',
        )
      }
    >
      {item.icon}
      <span>{t(item.labelKey)}</span>
    </NavLink>
  )
}

// ─── Footer status strip ─────────────────────────────────────────────
// Just a system-up indicator. No build hash, no environment label —
// users don't need (and shouldn't see) operational metadata.

function FooterStatus() {
  const { t } = useTranslation('common')
  return (
    <footer className="border-t border-line px-6 py-2 flex items-center text-xs text-textDim shrink-0">
      <span className="dot mr-2" style={{ background: 'var(--pulse)' }} />
      {t('status.operational')}
      <span className="ml-auto">{t('footer.copyright')}</span>
    </footer>
  )
}
